import { Permissions, type Permission } from '@/auth/permissions';

export interface NavSection {
  to: string;
  /** An i18n key (`nav.json`'s `nav.*`), not literal text — resolved with `t()` at render time. */
  labelKey: string;
  end?: boolean;
  /** Hidden unless the signed-in user holds this. Undefined means everyone sees it. */
  permission?: Permission;
  /** Set while the destination is still a placeholder, so the shell can say so. */
  milestone?: string;
}

/**
 * The product's main sections (M0-24).
 *
 * Several destinations are still placeholders. They are in the navigation
 * anyway because the shell's job is to show the shape of the product, and
 * because a section that appears only once its screen is built makes the
 * navigation move around under people as the app grows. Each placeholder says
 * which milestone builds it rather than pretending to be empty.
 *
 * ⚠️ Only the sections whose permissions the M0-22 matrix already defines are
 * gated. Each placeholder's own story added its gate along with the screen —
 * Customers at M1-04, Jobs at M1-15, the Pricebook at M2-02, Estimates at
 * M2-06 and Invoices at M2-12 — rather than a navigation file deciding a
 * product rule ahead of it.
 *
 * Dispatch (M1-09) is gated, because its own story built it: the board lists
 * every job in the account and every technician in it, which is `jobs.read.all`
 * plus `users.read` — a Technician holds neither, and the API refuses them
 * either way. It is a section of its own rather than a tab under Jobs: "the
 * day's assignments" and "the job record" are different questions, asked by
 * different people.
 */
/**
 * Routes that need the whole window rather than the shell's reading-width
 * column. Twenty-five technician columns inside a 64rem container is a board
 * that is almost entirely horizontal scrollbar — the constraint exists for
 * prose and forms, and the dispatch board is neither.
 */
export const wideRoutes: string[] = ['/dispatch'];

export function isWideRoute(pathname: string): boolean {
  return wideRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export const navSections: NavSection[] = [
  // jobs.read.all (M2-17), the gate the API reads the dashboard on: the
  // office only. A Technician's `/` is their job list, so they never see it.
  { to: '/', labelKey: 'nav.dashboard', end: true, permission: Permissions.readAllJobs },
  { to: '/dispatch', labelKey: 'nav.dispatch', permission: Permissions.readAllJobs },
  // jobs.work, not jobs.read.all (M1-22): the list narrows itself to the
  // caller's own assignments server-side, so a Technician on a phone finally
  // has a way to SEE their jobs rather than only opening one from a link.
  // Every office role holds jobs.work too, so nobody loses the section.
  { to: '/jobs', labelKey: 'nav.jobs', permission: Permissions.workAssignedJobs },
  { to: '/customers', labelKey: 'nav.customers', permission: Permissions.readCustomers },
  // estimates.build (M2-06), which every role holds: a technician's list is
  // the estimates on their own jobs, narrowed by the API.
  { to: '/estimates', labelKey: 'nav.estimates', permission: Permissions.buildEstimates },
  // customers.read (M2-12), the gate the API reads invoices on: office roles
  // only — a Technician neither sees the section nor could load it.
  { to: '/invoices', labelKey: 'nav.invoices', permission: Permissions.readCustomers },
  // customers.read (M2-02), the gate estimates read on: whoever can see an
  // estimate can see the book it was priced from. Changing it is
  // pricebook.manage, which the screen checks for itself.
  { to: '/pricebook', labelKey: 'nav.pricebook', permission: Permissions.readCustomers },
  { to: '/people', labelKey: 'nav.people', permission: Permissions.readUsers },
  // users.read (M1-27), the same gate the API uses — a Dispatcher must be able
  // to look a truck up, because the board's tooltip names it. Beside People
  // rather than under it: "who works here" and "what do we drive" are two
  // record sets, and the fleet has its own detail pages.
  { to: '/fleet', labelKey: 'nav.fleet', permission: Permissions.readUsers },
  // company.manage (M2-19): Owner and Admin edit the company's settings. The
  // Owner-only relationship-role catalog inside is hidden from the Admin.
  { to: '/settings', labelKey: 'nav.settings', permission: Permissions.manageCompany },
];
